'use client'

import { Suspense, useState, useEffect, FormEvent } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  Loader2,
  ShieldCheck,
  ArrowRight,
  AlertTriangle,
  Clock,
  User,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Mail,
  MailCheck,
} from 'lucide-react'
import LiquidGlassButton from '@/components/login/LiquidGlassButton'
import FluxoAnimado from '@/components/login/FluxoAnimado'
import { ProductMark } from '@/components/brand/ProductMark'
import { useProduct } from '@/components/brand/ProductProvider'
import { EXPIRACAO_MINUTOS } from '@/lib/auth/password-policy'

/**
 * Tela de login (Templates/TelaInicio.md): fundo animado de curvas
 * e pontos na tela toda (FluxoAnimado) e, por cima, uma caixa arredondada com a marca +
 * formulário.
 *
 * Marca, nome e COR vêm da identidade do produto — nunca de literal. Nome e
 * logotipo saem de `useProduct()` (a instalação sem logotipo próprio ganha um
 * wordmark em texto ao lado do símbolo neutro, em vez de uma imagem quebrada);
 * a cor sai dos tokens do design system, que o layout raiz já pinta com o
 * `--gr-hue` da instalação. Quem compra não entra mais por uma porta azul que
 * não é dele.
 *
 * Auth LOCAL, caminho único: e-mail + senha contra /api/auth/login. Não existe
 * login federado nem redirect automático — o formulário aparece já na primeira
 * renderização e nenhuma requisição sai pra fora do app.
 *
 * Quando já existe sessão local viva (/api/auth/me), a tela troca o formulário
 * pelo atalho "Entrar" com o nome do usuário.
 *
 * RECUPERAÇÃO DE SENHA
 * O "Esqueci minha senha" NÃO leva para outra página: troca o formulário aqui
 * mesmo. Quem esqueceu a senha está a um passo de desistir — tirá-lo da tela de
 * entrada, e depois obrigá-lo a voltar, é atrito puro. `?recuperar=1` abre já
 * nesse modo (é para onde a tela do link expirado manda), e `?senha=alterada`
 * é o aviso de volta depois da redefinição concluída.
 */

/**
 * A tela de login não tem paleta própria: ela lê os MESMOS tokens do resto do
 * produto. `--gr-hue` é plantado no <html> pelo layout raiz a partir da marca da
 * instalação, então trocar a cor do comprador repinta esta porta de entrada
 * junto com o app — sem tocar em nenhum literal aqui. Antes eram 22 hex de uma
 * paleta azul-ardósia que não tinha relação com nada.
 */
const ACENTO = 'var(--gr-accent)'

/** Destino padrão quando não veio link profundo (ou quando ele é inseguro). */
// A porta de entrada, não o inbox. Mandar direto para as conversas servia a quem só
// atende e era um beco para todo o resto — quem entra para ver relatório ou ir ao
// módulo de marketing caía numa lista e tinha que se orientar sozinho.
// Ver src/app/(app)/page.tsx.
const DEFAULT_REDIRECT = '/'

/**
 * Campo de texto dos dois formulários desta tela (entrar e recuperar).
 *
 * Aparência (fundo, borda, cor, hover e o anel de foco) vem da classe
 * `.gr-input` do design system — daí só sobrar aqui o que é específico deste
 * layout: altura de 44px e o recuo à esquerda que abre espaço pro ícone. Assim o
 * campo que recebe a senha do cliente mostra o mesmo foco visível do resto do
 * produto, em vez do `outline: none` mudo que estava aqui.
 */
const CAMPO: React.CSSProperties = {
  height: 44,
  width: '100%',
  borderRadius: 10,
  padding: '0 14px 0 38px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
}

/**
 * A ÚNICA frase que a tela mostra depois de pedir o link — a mesma exista ou não
 * a conta. Ela é o par visível da resposta única da API: adiantar "não achamos
 * esse e-mail" aqui desmontaria, na tela, o cuidado que a rota toma para não
 * virar um verificador de e-mails cadastrados.
 */
const AVISO_LINK_ENVIADO =
  'Se este e-mail estiver cadastrado, enviamos um link para redefinir a senha. '
  + 'Confira também a caixa de spam.'

/**
 * Sanitiza o destino pós-login vindo de ?redirect= / ?next=.
 *
 * Só aceita caminho INTERNO: precisa começar com '/', não pode começar com '//'
 * nem '/\' (o browser lê os dois como protocol-relative → outro host) e não pode
 * ser o próprio '/login' (laço). Qualquer outra coisa cai em '/inbox'.
 *
 * Esta regra vivia numa rota de redirect que foi removida; agora o único ponto
 * que consome o parâmetro é esta tela, então a validação mora aqui.
 */
function safeRedirect(raw: string | null): string {
  if (!raw) return DEFAULT_REDIRECT
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return DEFAULT_REDIRECT
  const path = raw.split(/[?#]/)[0]
  if (path === '/login') return DEFAULT_REDIRECT
  return raw
}

/** Mensagem de erro vinda por ?error= (hoje só o SessionKeepalive produz). */
function safeErrorMessage(raw: string | null): string | null {
  if (!raw) return null
  const msg = raw.trim().slice(0, 200)
  return msg || 'Não foi possível entrar. Fale com o administrador.'
}

export default function LoginPage() {
  return (
    <Suspense fallback={<BootSpinner />}>
      <LoginInner />
    </Suspense>
  )
}

function BootSpinner() {
  return (
    <div style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--gr-bg)' }}>
      <Loader2 size={20} className="animate-spin" style={{ color: ACENTO }} />
    </div>
  )
}

function LoginInner() {
  const searchParams = useSearchParams()
  const redirectTo = safeRedirect(searchParams.get('redirect') || searchParams.get('next'))
  const idleLogout = searchParams.get('reason') === 'idle'
  const loginError = safeErrorMessage(searchParams.get('error'))
  const senhaAlterada = searchParams.get('senha') === 'alterada'
  const year = new Date().getFullYear()
  const product = useProduct()

  // undefined = ainda checando sessão; null = sem sessão; objeto = já logado.
  // Enquanto está checando, a tela já mostra o formulário — nada de "Carregando…".
  const [me, setMe] = useState<{ display_name: string } | null | undefined>(undefined)

  // 'recuperar' quando a pessoa chegou de um link expirado (?recuperar=1) ou
  // clicou em "Esqueci minha senha". Fica nesta tela: é o mesmo assunto.
  const [modo, setModo] = useState<'login' | 'recuperar'>(
    searchParams.get('recuperar') === '1' ? 'recuperar' : 'login',
  )

  useEffect(() => {
    fetch('/api/auth/me', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d: { user: { display_name?: string } | null }) => {
        setMe(d.user ? { display_name: d.user.display_name || 'usuário' } : null)
      })
      .catch(() => setMe(null))
  }, [])

  return (
    <div
      className="relative flex items-center justify-center lg:justify-end overflow-hidden"
      style={{ minHeight: '100dvh', fontFamily: '"Sora", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}
    >
      {/* Fundo animado de curvas e pontos na tela toda. A cor fica em FluxoAnimado.tsx. */}
      <FluxoAnimado />

      {/* Caixa de login por cima do fundo, à direita no desktop e centralizada no celular. */}
      <div
        className="relative w-[calc(100%-2rem)] max-w-[400px] my-8 lg:mr-[10vw] flex flex-col items-center px-8 py-16 sm:px-10"
        style={{
          background: 'var(--gr-bg)',
          border: '1px solid var(--gr-border)',
          borderRadius: 24,
          boxShadow: 'var(--gr-shadow-3)',
        }}
      >
        <div className="mb-8 flex justify-center">
          {product.logoUrl ? (
            <>
              {/* <img> em vez de next/image: otimizador (sharp) falha no Windows. */}
              { }
              <img src={product.logoUrl} alt={product.name} style={{ width: 240, height: 'auto', objectFit: 'contain' }} />
            </>
          ) : (
            // Sem logotipo, o lockup é símbolo + wordmark em texto.
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span aria-hidden="true" style={{ color: ACENTO, display: 'flex' }}>
                <ProductMark size={38} symbolUrl={product.symbolUrl} title={product.name} />
              </span>
              <span
                style={{
                  fontSize: 34,
                  fontWeight: 600,
                  letterSpacing: '-0.035em',
                  lineHeight: 1,
                  color: 'var(--gr-text)',
                }}
              >
                {product.name}
              </span>
            </div>
          )}
        </div>

        {senhaAlterada && (
          <Banner kind="ok" icon={CheckCircle2}>
            Senha alterada. Entre com a nova senha — os acessos abertos em outros dispositivos foram encerrados.
          </Banner>
        )}
        {idleLogout && (
          <Banner kind="warn" icon={Clock}>Sessão expirada por inatividade. Autentique-se novamente.</Banner>
        )}
        {loginError && (
          <Banner kind="err" icon={AlertTriangle}>{loginError}</Banner>
        )}

        {me ? (
          <>
            <LiquidGlassButton type="button" onClick={() => { window.location.href = redirectTo }}>
              <ShieldCheck size={16} style={{ color: 'var(--gr-positive)' }} />
              Entrar
              <ArrowRight size={14} />
            </LiquidGlassButton>
            <p className="mt-4 text-center text-sm" style={{ color: 'var(--gr-text-2)' }}>
              Bem-vindo, <strong style={{ color: 'var(--gr-text)' }}>{me.display_name}</strong>!
            </p>
          </>
        ) : modo === 'recuperar' ? (
          <RecuperarSenhaForm onVoltar={() => setModo('login')} />
        ) : (
          <LocalLoginForm redirectTo={redirectTo} onEsqueci={() => setModo('recuperar')} />
        )}

        <p className="mt-8 text-center text-xs" style={{ color: 'var(--gr-text-3)' }}>{product.name} © {year}</p>
      </div>
    </div>
  )
}

function Banner({ kind, icon: Icon, children }: { kind: 'warn' | 'err' | 'ok'; icon: React.ElementType; children: React.ReactNode }) {
  // As três variantes falam pelos tokens de estado do design system. Estado é
  // estado em qualquer instalação: o verde do "deu certo" e o vermelho do "não
  // entrou" não mudam com a marca, mas mudam com o tema — e por isso vêm de
  // token, não do rgba fixo que estava aqui.
  const palette =
    kind === 'ok'
      ? {
          bg: 'var(--gr-positive-soft)',
          border: 'color-mix(in oklch, var(--gr-positive) 30%, transparent)',
          color: 'var(--gr-positive)',
        }
      : kind === 'warn'
      ? {
          bg: 'var(--gr-warning-soft)',
          border: 'color-mix(in oklch, var(--gr-warning) 30%, transparent)',
          color: 'var(--gr-warning)',
        }
      : {
          bg: 'var(--gr-danger-soft)',
          border: 'color-mix(in oklch, var(--gr-danger) 30%, transparent)',
          color: 'var(--gr-danger)',
        }
  return (
    <div
      className="mb-5 flex items-start gap-2 px-4 py-3 text-sm"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color, borderRadius: 12, maxWidth: 360 }}
    >
      <Icon size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  )
}

/** Login local — e-mail + senha contra /api/auth/login. Caminho único de entrada. */
function LocalLoginForm({ redirectTo, onEsqueci }: { redirectTo: string; onEsqueci: () => void }) {
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        // redirectTo já vem sanitizado por safeRedirect().
        router.push(data?.user?.must_change_password ? '/change-password' : redirectTo)
        router.refresh()
        return
      }
      if (res.status === 429) setError(`Muitas tentativas. Tente em ${res.headers.get('Retry-After') ?? '60'}s.`)
      else if (res.status === 503) setError('Sistema sem usuários cadastrados. Contate o administrador.')
      else setError(data?.error || 'Credenciais inválidas.')
    } catch (err) {
      setError((err as Error).message || 'Erro de rede. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputStyle = CAMPO

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%', maxWidth: 320 }}>
      <div style={{ position: 'relative' }}>
        <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gr-text-3)', pointerEvents: 'none' }} />
        <input type="email" className="gr-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu.email@empresa.com" required autoComplete="email" autoFocus disabled={submitting} style={inputStyle} />
      </div>
      <div style={{ position: 'relative' }}>
        <Lock size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gr-text-3)', pointerEvents: 'none' }} />
        <input type={showPassword ? 'text' : 'password'} className="gr-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" disabled={submitting} style={{ ...inputStyle, padding: '0 42px 0 38px' }} />
        <button type="button" onClick={() => setShowPassword((v) => !v)} tabIndex={-1} aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'} style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 10, cursor: 'pointer', color: 'var(--gr-text-3)', display: 'flex' }}>
          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error && (
        <div role="alert" style={{ background: 'var(--gr-danger-soft)', border: '1px solid color-mix(in oklch, var(--gr-danger) 30%, transparent)', color: 'var(--gr-danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{error}</span>
        </div>
      )}
      {/* O MESMO botão do atalho de reentrar, logo acima nesta tela.
          Até 14/09/2026 eram dois: o de vidro aparecia só para quem já tinha
          sessão, e o caminho normal — e-mail, senha, Entrar — usava um retângulo
          chapado. Dois botões com o mesmo rótulo e dois desenhos, na mesma tela.
          O dono viu e perguntou se não dava para ser sempre assim. */}
      <LiquidGlassButton type="submit" disabled={submitting || !email.trim() || !password}>
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={16} style={{ color: 'var(--gr-positive)' }} />}
        {submitting ? 'Entrando…' : 'Entrar'}
        {!submitting && <ArrowRight size={14} />}
      </LiquidGlassButton>

      {/* Abaixo do botão, e não escondido num canto: é o segundo caminho mais
          usado desta tela, depois do próprio Entrar. */}
      <button
        type="button"
        onClick={onEsqueci}
        disabled={submitting}
        style={{
          // 36px de altura: alvo de toque que se acerta no celular sem mira.
          minHeight: 36,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          padding: '0 8px',
          margin: '-4px auto 0',
          fontSize: 13,
          fontFamily: 'inherit',
          color: 'var(--gr-text-2)',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}
      >
        Esqueci minha senha
      </button>
    </form>
  )
}

/**
 * Pedido do link de redefinição.
 *
 * Depois de enviar, a tela mostra SEMPRE a mesma confirmação — cadastrado ou
 * não. É o par visível da resposta única da rota: se aqui aparecesse "e-mail não
 * encontrado", a proteção contra enumeração de contas do servidor estaria
 * desfeita pela própria interface.
 */
function RecuperarSenhaForm({ onVoltar }: { onVoltar: () => void }) {
  const [email, setEmail] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [enviado, setEnviado] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setErro(null)
    setEnviando(true)
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      if (res.ok) {
        setEnviado(true)
        return
      }
      const dados = await res.json().catch(() => ({}))
      if (res.status === 429) {
        setErro(`Muitos pedidos. Tente de novo em ${res.headers.get('Retry-After') ?? '60'}s.`)
      } else {
        setErro(dados?.error || 'Não foi possível enviar o link. Tente novamente.')
      }
    } catch (err) {
      setErro((err as Error).message || 'Erro de rede. Tente novamente.')
    } finally {
      setEnviando(false)
    }
  }

  if (enviado) {
    return (
      <div style={{ width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            background: 'var(--gr-positive-soft)',
            color: 'var(--gr-positive)',
            borderRadius: 12,
            padding: '12px 14px',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <MailCheck size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{AVISO_LINK_ENVIADO}</span>
        </div>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--gr-text-2)' }}>
          O link vale por {EXPIRACAO_MINUTOS} minutos e só pode ser usado uma vez.
        </p>
        <VoltarParaLogin onVoltar={onVoltar} />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%', maxWidth: 320 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--gr-text)', marginBottom: 4 }}>Recuperar acesso</div>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--gr-text-2)' }}>
          Informe o e-mail da sua conta. Enviaremos um link para você criar uma senha nova.
        </p>
      </div>

      <div style={{ position: 'relative' }}>
        <Mail size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--gr-text-3)', pointerEvents: 'none' }} />
        <input
          type="email"
          className="gr-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="seu.email@empresa.com"
          required
          autoComplete="email"
          autoFocus
          disabled={enviando}
          style={CAMPO}
        />
      </div>

      {erro && (
        <div role="alert" style={{ background: 'var(--gr-danger-soft)', border: '1px solid color-mix(in oklch, var(--gr-danger) 30%, transparent)', color: 'var(--gr-danger)', borderRadius: 10, padding: '10px 12px', fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{erro}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={enviando || !email.trim()}
        style={{
          height: 44,
          background: ACENTO,
          color: 'var(--gr-text-on-accent)',
          border: 'none',
          borderRadius: 10,
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'inherit',
          cursor: enviando ? 'not-allowed' : 'pointer',
          opacity: !email.trim() && !enviando ? 0.5 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        {enviando && <Loader2 size={14} className="animate-spin" />}
        {enviando ? 'Enviando…' : 'Enviar link de redefinição'}
      </button>

      <VoltarParaLogin onVoltar={onVoltar} />
    </form>
  )
}

function VoltarParaLogin({ onVoltar }: { onVoltar: () => void }) {
  return (
    <button
      type="button"
      onClick={onVoltar}
      style={{
        minHeight: 36,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        background: 'none',
        border: 'none',
        padding: '0 8px',
        margin: '-4px auto 0',
        fontSize: 13,
        fontFamily: 'inherit',
        color: 'var(--gr-text-2)',
        cursor: 'pointer',
      }}
    >
      <ArrowLeft size={14} />
      Voltar para o login
    </button>
  )
}
